import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import HeroSlider from './HeroSlider';
import PublicNotices from './PublicNotices';
import PublicVideos from './PublicVideos';
import PublicImageGalleries from './PublicImageGalleries';
import { useTranslation } from 'react-i18next';

interface Notice {
  id: number;
  title: string;
  content: string;
  type: string;
  created_at: string;
}

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  type: string;
}

const PublicHome: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [noticesRes, eventsRes] = await Promise.all([
          api.get('/public/notices'),
          api.get('/public/events')
        ]);
        setNotices(noticesRes.data);
        setEvents(eventsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">{t('public.loading')}</div>;
  }

  const features = [
    {
      title: t('public.features.noticesTitle'),
      description: t('public.features.noticesDescription'),
    },
    {
      title: t('public.features.eventsTitle'),
      description: t('public.features.eventsDescription'),
    },
    {
      title: t('public.features.paymentsTitle'),
      description: t('public.features.paymentsDescription'),
    }
  ];

  return (
    <div className="public-home">
      <HeroSlider />

      <section className="features">
        <div className="feature-grid">
          {features.map((feature) => (
            <div className="feature-card" key={feature.title}>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="notices-section">
        <PublicNotices />
      </section>

      <section className="videos-section">
        <PublicVideos />
      </section>

      <section className="galleries-section">
        <PublicImageGalleries />
      </section>

      <section className="events-section">
        <h2>{t('public.events.title')}</h2>
        <div className="events-grid">
          {events.map(event => (
            <div key={event.id} className="event-card">
              <h3>{event.title}</h3>
              <p>{event.description}</p>
              <small>{new Date(event.date).toLocaleDateString(i18n.language)}</small>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PublicHome;
